import { Cell, Pie, PieChart, ResponsiveContainer, Tooltip } from 'recharts';

import { XStack, YStack } from '@/components/ui/layout/layout';

import { UsageCard, UsageCardEmpty } from './UsageCard';
import { UsageChartTooltip } from './UsageChartTooltip';
import { useChartColors } from './useChartColors';

import type { TeamUsageResponse } from '../../team.types';

const INTEGRATION_LABELS: Record<string, string> = {
  gmail: 'Gmail',
  googlecalendar: 'Google Calendar',
  googledrive: 'Google Drive',
  googledocs: 'Google Docs',
  googlesheets: 'Google Sheets',
  github: 'GitHub',
  mcp: 'MCP',
  other: 'Other',
};

function integrationLabel(app: string): string {
  return INTEGRATION_LABELS[app] ?? app.charAt(0).toUpperCase() + app.slice(1);
}

export function TopIntegrationsCard({
  topIntegrations,
  totalActions,
}: {
  topIntegrations: TeamUsageResponse['top_integrations'];
  totalActions: number;
}) {
  const colors = useChartColors();
  const segmentColor = (index: number) =>
    colors.segments[index % colors.segments.length];
  const pctOf = (actions: number) =>
    totalActions ? Math.round((actions / totalActions) * 100) : 0;

  return (
    <UsageCard
      title="Top integrations"
      subtitle="Share of integration actions by app"
    >
      {topIntegrations.length === 0 ? (
        <UsageCardEmpty
          title="No integration actions"
          description="Nothing ran through integrations in this period."
        />
      ) : (
        <XStack wrap className="flex-1 content-center items-center gap-5">
          <div className="relative mx-auto size-[9.375rem] shrink-0">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={topIntegrations.map((integration) => ({
                    name: integrationLabel(integration.app),
                    value: integration.actions,
                  }))}
                  dataKey="value"
                  nameKey="name"
                  innerRadius="64%"
                  outerRadius="100%"
                  stroke={colors.surface}
                  strokeWidth={2}
                >
                  {topIntegrations.map((integration, i) => (
                    <Cell key={integration.app} fill={segmentColor(i)} />
                  ))}
                </Pie>
                <Tooltip
                  wrapperStyle={{ zIndex: 10 }}
                  content={({ active, payload }) =>
                    active && payload?.length ? (
                      <UsageChartTooltip>
                        {`${payload[0].name}: ${pctOf(Number(payload[0].value))}%`}
                      </UsageChartTooltip>
                    ) : null
                  }
                />
              </PieChart>
            </ResponsiveContainer>
            <YStack center className="pointer-events-none absolute inset-0">
              <span className="text-xl font-semibold text-foreground">
                {totalActions.toLocaleString()}
              </span>
              <span className="text-xs text-foreground-muted">actions</span>
            </YStack>
          </div>
          <YStack className="min-w-[10.5rem] flex-1 gap-2">
            {topIntegrations.map((integration, i) => (
              <XStack
                key={integration.app}
                className="items-center gap-2.5 text-sm"
              >
                <span
                  className="h-2.5 w-2.5 rounded-sm"
                  style={{ backgroundColor: segmentColor(i) }}
                />
                <span className="min-w-0 flex-1 truncate text-foreground">
                  {integrationLabel(integration.app)}
                </span>
                <span className="tabular-nums text-foreground-muted">
                  {integration.actions.toLocaleString()}
                </span>
                <span className="w-9 text-right tabular-nums text-foreground-muted">
                  {pctOf(integration.actions)}%
                </span>
              </XStack>
            ))}
          </YStack>
        </XStack>
      )}
    </UsageCard>
  );
}
